import type { RevaluationMode, RevaluationItem } from '@/types/revaluation';

/**
 * Calculate the revalued NBV for a single item based on the revaluation mode and amount
 */
export function calculateRevaluedNBV(
  currentNBV: number,
  mode: RevaluationMode,
  amount: number
): number {
  let result: number;

  switch (mode) {
    case 'ByPercentage':
      // Convert percentage to factor (e.g., 5 => 0.05, -2.75 => -0.0275)
      const factor = amount / 100;
      result = currentNBV * (1 + factor);
      break;

    case 'ByValue':
      // Add/subtract fixed amount
      result = currentNBV + amount;
      break;

    case 'ToValue':
      // Set to exact target value
      result = amount;
      break;

    default:
      result = currentNBV;
  }

  // Round to 2 decimal places (standard currency rounding: half up)
  return Math.round(result * 100) / 100;
}

/**
 * Validate revaluation amount based on mode
 */
export function validateRevaluationAmount(
  amount: number | string,
  mode: RevaluationMode
): { isValid: boolean; error?: string } {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (isNaN(numAmount)) {
    return { isValid: false, error: 'Enter a valid number.' };
  }

  // For 'To Value', enforce non-negative constraint (configurable)
  if (mode === 'ToValue' && numAmount < 0) {
    return { isValid: false, error: "For 'To Value', the amount must be ≥ 0." };
  }

  return { isValid: true };
}

/**
 * Get placeholder text for revaluation amount input based on mode
 */
export function getRevaluationAmountPlaceholder(mode: RevaluationMode): string {
  switch (mode) {
    case 'ByPercentage':
      return 'e.g., 5 for +5%';
    case 'ByValue':
      return 'e.g., 100.00 or -75.50';
    case 'ToValue':
      return 'e.g., 1,000.00';
    default:
      return '';
  }
}

/**
 * Get help text for revaluation mode
 */
export function getRevaluationModeHelp(mode: RevaluationMode): string {
  switch (mode) {
    case 'ByPercentage':
      return 'Apply an uplift/downlift by a percentage of current NBV';
    case 'ByValue':
      return 'Add/subtract a fixed amount to current NBV';
    case 'ToValue':
      return 'Set NBV to an exact target value';
    default:
      return '';
  }
}

/**
 * Recalculate all items in a batch based on the revaluation mode and amount
 * @param items - Array of revaluation items
 * @param mode - Revaluation mode
 * @param amount - Revaluation amount
 * @param applyToAll - If true, apply to all items; if false, only apply to non-overridden items
 * @returns Updated array of items
 */
export function recalculateRevaluationItems(
  items: RevaluationItem[],
  mode: RevaluationMode,
  amount: number,
  applyToAll: boolean = false
): RevaluationItem[] {
  return items.map(item => {
    // Skip overridden items unless applyToAll is true
    if (item.isOverridden && !applyToAll) {
      return item;
    }

    const revaluedNBV = calculateRevaluedNBV(item.currentNBV, mode, amount);
    const revaluationAdjustment = revaluedNBV - item.currentNBV;

    return {
      ...item,
      revaluedNBV,
      revaluationAdjustment,
      aiRevaluedNBV: revaluedNBV, // Update AI suggestion to match calculated value
      isOverridden: false, // Reset override flag when recalculating
    };
  });
}
